import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/store/hooks";

// Assets
import Timer from "../../components/timer";

// Testing things
import StatusBar from "../../components/status-bar";

// Types
import type { PauseType } from "src/types";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { requestPause, requestUnpause } from "src/store/auth/reducer";
import IcomBtn from "src/components/icom-btn";
import Swal from "sweetalert2";
import { updateLastStatusDate } from "src/store/context/reducer";
import Messages from "./messages";
import { trackEvent } from "src/services/logger";

export default function PauseComponent() {
  const { paused } = useAppSelector((state) => state.auth);
  const { availablePauseTypes, lastStatusUpdate } = useAppSelector((state) => state.context);

  const dispatch = useAppDispatch();

  const [state, setState] = useState<{
    selectedPause: string;
    availablePauseTypes: Array<PauseType>;
    pauseDropActive: boolean;
    fromTimestamp: number | null;
  }>({
    selectedPause: paused?.id || "0",
    availablePauseTypes: availablePauseTypes,
    pauseDropActive: true,
    fromTimestamp: null,
  });

  const togglePauseHandler = async (pauseType?: PauseType) => {
    const verb = pauseType ? "aplicar" : "remover";
    const capitalized = verb[0].toUpperCase() + verb.slice(1);

    const result = await Swal.fire({
      title: `${capitalized} pausa?`,
      text: `Confirmar?`,

      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: `Sim`,
      cancelButtonText: "Não",
    });

    if (!result.isConfirmed) return false;

    if (paused && !pauseType) {
      setState((s) => ({ ...s, fromTimestamp: null, selectedPause: "0" }));

      // Request pause
      dispatch(requestUnpause());
      dispatch(updateLastStatusDate(null));
      trackEvent("pause_ended");
    } else if (pauseType) {
      if (pauseType.id === "0") return alert("Selecione um tipo de pausa");

      // Pause
      const pauseObj = state.availablePauseTypes.find((p) => p.id == pauseType.id);

      // Request pause
      dispatch(requestPause(pauseObj));
      trackEvent("pause_started");

      setState((s) => ({ ...s, selectedPause: pauseType.id }));
    }
  };

  useEffect(() => {
    if (lastStatusUpdate && paused) {
      setState((s) => ({ ...s, fromTimestamp: lastStatusUpdate }));
    } else {
      setState((s) => ({ ...s, fromTimestamp: null }));
    }
  }, [lastStatusUpdate]);

  return (
    <div className="pause-screen p-8 pt-4 gap-8 flex flex-col" data-testid="initial-screen">
      <Messages />
      <StatusBar />

      {paused && (
        <div className="pause-actions items-center bg-primary-blue-800 dark:bg-primitive-blue-700 text-white px-6 py-5 rounded-md flex justify-between">
          <span className="text-lg font-bold">Pausa em andamento</span>

          <div className="flex items-center gap-8">
            <span className="text-base font-bold">{paused.texto}</span>
            <span className="vertical-hr w-[1px] bg-white h-[20px]"></span>
            <span className="text-base font-bold">{state.fromTimestamp ? <Timer fromTimestamp={state.fromTimestamp} /> : "00:00:00"}</span>
            <IcomBtn size="large" bordered className="bg-white border-none" onClick={() => togglePauseHandler()}>
              Finalizar pausa
            </IcomBtn>
          </div>
        </div>
      )}

      {!paused && (
        <div className="flex flex-col gap-5 bg-primary-blue-400 dark:bg-primitive-blue-500 px-6 py-5 rounded-lg">
          <button
            type="button"
            onClick={() =>
              setState((s) => ({
                ...s,
                pauseDropActive: !state.pauseDropActive,
              }))
            }
            className="text-white items-center w-full flex justify-between gap-5"
          >
            <span className="text-lg font-bold">Aplicar pausa</span>

            {state.pauseDropActive ? <FaChevronUp /> : <FaChevronDown />}
          </button>

          {state.pauseDropActive && (
            <div className="flex gap-2 flex-wrap">
              {state.availablePauseTypes.map(
                (p) =>
                  p.show && (
                    <button
                      type="button"
                      className="bg-primary-blue-800 w-fit text-white text-base font-bold px-4 py-1 rounded-lg whitespace-nowrap hover:bg-feedback-negative-500 transition-all"
                      key={p.id}
                      onClick={() => togglePauseHandler(p)}
                    >
                      {p.texto}
                    </button>
                  ),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
